import { useEffect, useRef, useState } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'
import { applyTheme, loadTheme, resolvedTheme, saveTheme, type Theme } from '../theme'

const OPTIONS: { value: Theme; label: string; hint: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', hint: 'Always light', Icon: Sun },
  { value: 'dark', label: 'Dark', hint: 'Always dark', Icon: Moon },
  { value: 'system', label: 'Auto', hint: 'Follow your device', Icon: Monitor },
]

/**
 * Theme control: one icon, expanding to three choices.
 *
 * A single cycling button would be smaller still, but cycling hides what comes
 * next — you click and find out. The menu costs one extra click and removes the
 * guessing.
 *
 * The trigger shows what you are actually looking at (sun or moon) rather than
 * which mode is selected, because on Auto the mode is not the useful
 * information. Which mode is selected is shown inside the menu.
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => loadTheme())
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    applyTheme(theme)
    saveTheme(theme)
  }, [theme])

  // Close on an outside click or Escape. Without the Escape handler the menu is
  // a keyboard trap for anyone not using a mouse.
  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const showing = resolvedTheme(theme)
  const TriggerIcon = showing === 'dark' ? Moon : Sun

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Colour theme: ${OPTIONS.find((o) => o.value === theme)?.label}`}
        title="Colour theme"
        className="grid size-8 place-items-center rounded-lg text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink"
      >
        <TriggerIcon size={16} strokeWidth={2} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-lg"
        >
          {OPTIONS.map(({ value, label, hint, Icon }) => {
            const active = theme === value
            return (
              <button
                key={value}
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setTheme(value)
                  setOpen(false)
                }}
                className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors ${
                  active ? 'bg-accent-soft text-accent-2' : 'text-ink-2 hover:bg-surface-2'
                }`}
              >
                <Icon size={14} strokeWidth={2} className="shrink-0" />
                <span className="flex-1">
                  <span className="block text-[12.5px] font-semibold">{label}</span>
                  <span className="block text-[10.5px] text-ink-3">{hint}</span>
                </span>
                {active && <span className="text-[11px] font-bold">&#10003;</span>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
